import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

class MochiCartUpsellPicker extends HTMLElement {
  connectedCallback() {
    if (this.listenersAttached) return;
    this.listenersAttached = true;

    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartLinesUpdate);
    this.updateVariant();
    this.variantObserver = new MutationObserver((records) => {
      // Section morphs can replace options or reset the hidden variant id without
      // reconnecting this element. Ignore loading/animation changes to the button.
      const selector = '[data-upsell-option], [data-upsell-variant-id], [data-upsell-variants]';
      const selectionChanged = records.some((record) => {
        const target = record.target instanceof Element ? record.target : record.target.parentElement;
        if (target?.closest(selector)) return true;
        return [...record.addedNodes, ...record.removedNodes].some((node) =>
          node instanceof Element && (node.matches(selector) || node.querySelector(selector))
        );
      });
      if (!selectionChanged) return;
      this.variantObserver.disconnect();
      this.updateVariant();
      this.observeVariantMarkup();
    });
    this.observeVariantMarkup();
  }

  disconnectedCallback() {
    this.variantObserver?.disconnect();
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.handleCartLinesUpdate);
    this.listenersAttached = false;
  }

  observeVariantMarkup() {
    this.variantObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['selected', 'value', 'data-option-index'],
    });
  }

  handleClick = (event) => {
    const revealButton = event.target instanceof Element ? event.target.closest('[data-upsell-reveal]') : null;
    if (!revealButton || !this.contains(revealButton)) return;

    const controls = this.querySelector('[data-upsell-controls]');
    const firstSelect = this.querySelector('[data-upsell-option]');
    if (!(controls instanceof HTMLElement) || !(revealButton instanceof HTMLButtonElement)) return;

    controls.hidden = false;
    revealButton.setAttribute('aria-expanded', 'true');
    revealButton.hidden = true;

    requestAnimationFrame(() => {
      controls.scrollIntoView({ block: 'nearest' });
      if (firstSelect instanceof HTMLSelectElement) firstSelect.focus({ preventScroll: true });
    });
  };

  handleChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-upsell-option]')) return;
    this.updateVariant();
  };

  handleCartLinesUpdate = (event) => {
    if (event.action !== 'add' || !(event.target instanceof Node) || !this.contains(event.target)) return;
    if (this.dataset.duoEnabled !== 'true' || !this.dataset.duoDiscountCode) return;

    event.promise
      ?.then(({ cart, detail }) => {
        if (!detail?.didError) return this.applyDuoDiscount(cart);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[mochi-duo] Add event rejected:', error);
      });
  };

  get variants() {
    try {
      return JSON.parse(this.querySelector('[data-upsell-variants]')?.textContent || '[]');
    } catch (_error) {
      return [];
    }
  }

  get selects() {
    return [...this.querySelectorAll('[data-upsell-option]')];
  }

  updateVariant() {
    const selects = this.selects;
    if (selects.length === 0) return;

    const selectedOptions = [];
    let selectionComplete = true;

    selects.forEach((select) => {
      const optionIndex = Number(select.dataset.optionIndex);
      selectedOptions[optionIndex] = select.value.trim();
      if (!select.value) selectionComplete = false;
    });

    if (!selectionComplete) {
      this.setState(null, (window.mochiscreenI18n?.choisir_les_options_df6cd6_html || 'Choisir les options'));
      return;
    }

    const variant = this.variants.find((candidate) => {
      if (!Array.isArray(candidate.options) || candidate.options.length !== selectedOptions.length) return false;
      return candidate.options.every((option, index) => String(option).trim() === selectedOptions[index]);
    });

    if (!variant?.available) {
      this.setState(null, (window.mochiscreenI18n?.indisponible_c04980_html || 'Indisponible'));
      return;
    }

    const addLabel = this.dataset.addLabel || (this.dataset.duoEnabled === 'true' ? this.dataset.duoButtonLabel || (window.mochiscreenI18n?.ajouter_au_duo_d11bb4_html || 'Ajouter au Duo') : (window.mochiscreenI18n?.ajouter_daff7a_html || '+ Ajouter'));
    this.setState(variant, addLabel);
  }

  setState(variant, label) {
    const variantInput = this.querySelector('[data-upsell-variant-id]');
    const button = this.querySelector('[ref="addToCartButton"]');
    const buttonLabel = button?.querySelector('.add-to-cart-text__content > span > span');
    const price = this.querySelector('[data-upsell-price]');
    const originalPrice = this.querySelector('[data-upsell-original-price]');

    if (variantInput instanceof HTMLInputElement) variantInput.value = variant?.id || '';
    if (button instanceof HTMLButtonElement) button.disabled = !variant;
    if (buttonLabel) buttonLabel.textContent = label;

    const error = this.querySelector('[ref="addToCartTextError"]');
    if (error) {
      error.textContent = '';
      error.classList.add('hidden');
    }

    if (!variant) return;

    const savingCents = this.dataset.duoEnabled === 'true' ? Number(this.dataset.duoSavingCents || 0) : 0;
    if (originalPrice) originalPrice.textContent = this.formatMoney(variant.price);
    if (price) price.textContent = this.formatMoney(Math.max(0, variant.price - savingCents));
  }

  async applyDuoDiscount(cart) {
    const discountCode = this.dataset.duoDiscountCode?.trim();
    if (!discountCode) return;

    const existingCodes = (cart?.discountCodes || [])
      .map((discount) => discount.code)
      .filter(Boolean);
    const allCodes = [...new Set([...existingCodes, discountCode])];
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allCodes.join(','),
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      const response = await fetch(Theme.routes.cart_update_url, config);
      if (!response.ok) throw new Error(`Discount update failed: ${response.status}`);

      const data = await response.json();

      sectionIds.forEach((sectionId) => {
        if (!data.sections?.[sectionId]) return;
        morphSection(sectionId, data.sections[sectionId], {
          mode: sectionId === 'cart-drawer-section' ? 'hydration' : 'full',
        });
      });
    } catch (error) {
      console.warn('[mochi-duo] Unable to apply discount:', error);
    }
  }

  formatMoney(cents) {
    const locale = this.dataset.locale || 'fr-FR';
    const currency = this.dataset.currency || 'EUR';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  }
}

if (!customElements.get('mochi-cart-upsell-picker')) {
  customElements.define('mochi-cart-upsell-picker', MochiCartUpsellPicker);
}

// A native disclosure keeps browsing optional; the cart summary stays outside it.
class MochiCartCoquesBrowser extends HTMLElement {
  connectedCallback() {
    if (this.listenersAttached) return;
    this.listenersAttached = true;
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.listenersAttached = false;
  }

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const close = event.target.closest('[data-coques-close]');
    if (close && this.contains(close)) {
      this.closeBrowser();
      return;
    }
    const tab = event.target.closest('[data-coques-tab]');
    if (tab && this.contains(tab)) this.selectTab(tab);
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.querySelector('[data-coques-details]')?.open) {
      event.preventDefault();
      event.stopPropagation();
      this.closeBrowser();
      return;
    }
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[data-coques-tab]');
    if (!tab || !this.contains(tab)) return;
    const tabs = [...this.querySelectorAll('[data-coques-tab]')];
    const currentIndex = tabs.indexOf(tab);
    let nextIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    else return;
    event.preventDefault();
    this.selectTab(tabs[nextIndex]);
    tabs[nextIndex].focus({ preventScroll: true });
    tabs[nextIndex].scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  selectTab(tab) {
    const productId = tab.dataset.coquesTab;
    this.querySelectorAll('[data-coques-tab]').forEach((candidate) => {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', String(selected));
      candidate.tabIndex = selected ? 0 : -1;
    });
    this.querySelectorAll('[data-coques-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.coquesPanel !== productId;
    });
  }

  closeBrowser() {
    const details = this.querySelector('[data-coques-details]');
    if (details instanceof HTMLDetailsElement) details.open = false;
    this.querySelector('[data-coques-summary]')?.focus({ preventScroll: true });
  }
}

if (!customElements.get('mochi-cart-coques-browser')) {
  customElements.define('mochi-cart-coques-browser', MochiCartCoquesBrowser);
}
